import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '@helpers/api';

export default function useSubmissionDetails({ submissionId, communityId }) {
    const [searchParams] = useSearchParams();
    const formId = searchParams.get('form_id');

    // Core submission + steps
    const [submission, setSubmission] = useState(null);
    const [loadingSubmission, setLoadingSubmission] = useState(true);

    // User history for this form
    const [history, setHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);

    // Currently selected submission answers (may differ from initial when user navigates history)
    const [selectedSubmissionId, setSelectedSubmissionId] = useState(submissionId);
    const [selectedSteps, setSelectedSteps] = useState([]);
    const [loadingAnswers, setLoadingAnswers] = useState(false);

    // Other community submissions for this user
    const [otherSubmissions, setOtherSubmissions] = useState([]);
    const [loadingOther, setLoadingOther] = useState(false);
    const [otherPage, setOtherPage] = useState(1);
    const [otherPages, setOtherPages] = useState(1);
    const [otherTotal, setOtherTotal] = useState(0);

    const [activeTab, setActiveTab] = useState('history');

    useEffect(() => {
        if (submissionId && communityId && formId) {
            loadSubmission(submissionId);
        }
    }, [submissionId, communityId, formId]);

    // Load history + other submissions once we know the user
    useEffect(() => {
        if (!submission?.user?.id || !communityId || !formId) return;
        loadHistory(submission.user.id);
        loadOtherSubmissions(1, submission.user.id);
    }, [submission?.user?.id, communityId, formId]);

    async function loadSubmission(id) {
        setLoadingSubmission(true);
        try {
            const { data } = await api.get(`forms/${formId}/submissions/${id}`);
            setSubmission(data?.submission ?? null);
            setSelectedSteps(data?.steps ?? []);
            setSelectedSubmissionId(id);
        } catch (e) {
            console.error(e);
        }
        setLoadingSubmission(false);
    }

    async function loadHistory(userId) {
        setLoadingHistory(true);
        try {
            const { data } = await api.get(
                `communities/${communityId}/forms/submissions`,
                { user_id: userId, form_id: formId, per_page: 50 }
            );
            setHistory(data?.data ?? []);
        } catch (e) {
            console.error(e);
        }
        setLoadingHistory(false);
    }

    async function loadOtherSubmissions(page = 1, userId) {
        const uid = userId ?? submission?.user?.id;
        if (!uid) return;
        setLoadingOther(true);
        try {
            const { data } = await api.get(
                `communities/${communityId}/forms/submissions`,
                { user_id: uid, exclude_form_id: formId, page, per_page: 8 }
            );
            setOtherSubmissions(data?.data ?? []);
            setOtherPages(data?.meta?.last_page ?? 1);
            setOtherTotal(data?.meta?.total ?? 0);
            setOtherPage(page);
        } catch (e) {
            console.error(e);
        }
        setLoadingOther(false);
    }

    async function handleSelectSubmission(sub) {
        if (sub.id === selectedSubmissionId || loadingAnswers) return;
        setSelectedSubmissionId(sub.id);
        setLoadingAnswers(true);
        try {
            const { data } = await api.get(`forms/${formId}/submissions/${sub.id}`);
            setSelectedSteps(data?.steps ?? []);
        } catch (e) {
            console.error(e);
        }
        setLoadingAnswers(false);
    }

    // Merge richer user display name from history list (detail only returns id + email)
    const userDisplay = (() => {
        const base = submission?.user ?? {};
        const fromHistory = history.find(h => h.user?.id === base.id)?.user;
        return { ...base, username: fromHistory?.username ?? base.email ?? '—' };
    })();

    // Form info comes from history list (detail has none)
    const formInfo = history[0]?.form ?? null;

    return {
        submission,
        userDisplay,
        formInfo,
        loadingSubmission,
        history,
        loadingHistory,
        selectedSubmissionId,
        selectedSteps,
        loadingAnswers,
        otherSubmissions,
        loadingOther,
        otherPage,
        otherPages,
        otherTotal,
        activeTab,
        setActiveTab,
        handleSelectSubmission,
        handleOtherPage: (page) => loadOtherSubmissions(page),
    };
}
